import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import DeTimeline from '../../components/datamanagement/DeTimeline';
import DeRightSidebar from '../../components/datamanagement/DeRightSidebar';
import { useDataEntry } from '../../queries/dataEntryQueries';

/**
 * ClearingChargesPage — Clearing Charges statement matching legacy software 100%.
 *
 * Route: /business/clearing-charges/:id
 * Active Step in Timeline: 2 (CLEARING CHARGES)
 */
const ClearingChargesPage = () => {
  const { id } = useParams();
  const { data: retrievedData, isLoading, isError } = useDataEntry(id);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleDownloadPdf = () => {
    const element = document.getElementById('clear_changess');
    if (!element) return;

    setIsProcessing(true);

    const opt = {
      margin: 8,
      filename: `ClearingCharges_${retrievedData?.fileName || id}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        scrollY: 0,
        windowWidth: document.documentElement.scrollWidth,
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
    };

    html2pdf()
      .set(opt)
      .from(element)
      .save()
      .then(() => setIsProcessing(false))
      .catch(() => setIsProcessing(false));
  };

  // Container Count
  const containerCount =
    Number(retrievedData?.containersNumber) ||
    (Array.isArray(retrievedData?.userForm) ? retrievedData.userForm.length : 0) ||
    (Array.isArray(retrievedData?.dataEntryForm) ? retrievedData.dataEntryForm.length : 0) ||
    0;

  // PQ Weight
  const pqW = Number(retrievedData?.pqWeightcheck) || 0;
  const pqAmount = pqW > 0 ? (pqW - 1) * 200 + 3500 : 0;

  // Rates & Amounts matching legacy code (using rate * containersNumber if total amount is not directly saved)
  const cfsRate = Number(retrievedData?.cfsChargesNew) || 0;
  const cfsAmt = Number(retrievedData?.cfsCharges) || (cfsRate * containerCount);

  const chaRate = Number(retrievedData?.chaSurveyNew) || 0;
  const chaAmt = Number(retrievedData?.chaSurvey) || (chaRate * containerCount);

  const examRate = Number(retrievedData?.examinationChargenew) || 0;
  const examAmt = Number(retrievedData?.examinationCharge) || (examRate * containerCount);

  const groundRate = Number(retrievedData?.cfsGroundRentNew) || 0;
  const groundAmt = Number(retrievedData?.cfsGroundRent) || (groundRate * containerCount);

  const sec49Rate = Number(retrievedData?.sec49New) || 0;
  const sec49Amt = Number(retrievedData?.sec49) || (sec49Rate * containerCount);

  const fumiRate = Number(retrievedData?.fumigationChargeNew) || 0;
  const fumiAmt = Number(retrievedData?.fumigationCharges || retrievedData?.fumigationCharge) || (fumiRate * containerCount);

  const pqFumi = Number(retrievedData?.pqFumigation) || 0;
  const pqWeightTimes = Number(retrievedData?.pqWeight) || 0;

  // Table rows array template matching old software exactly
  const formData = [
    {
      id: 1,
      description: 'PQ CHARGES',
      NOS: pqW > 0 ? pqW : 0,
      RATE: '',
      AMT: pqAmount,
    },
    {
      id: 2,
      description: 'PQ RELAX',
      NOS: '',
      RATE: '',
      AMT: pqFumi,
    },
    {
      id: 3,
      description: 'PQ 4 TIMES',
      NOS: '',
      RATE: '',
      AMT: pqWeightTimes,
    },
    {
      id: 4,
      description: 'FUMI CHARGES',
      NOS: containerCount > 0 ? containerCount : 0,
      RATE: '',
      AMT: fumiAmt,
    },
    {
      id: 5,
      description: 'CFS',
      NOS: containerCount > 0 ? containerCount : 0,
      RATE: cfsRate > 0 ? cfsRate : 0,
      AMT: cfsAmt,
    },
    {
      id: 6,
      description: 'CHA & SURVEY',
      NOS: containerCount > 0 ? containerCount : 0,
      RATE: chaRate > 0 ? chaRate : 0,
      AMT: chaAmt,
    },
    {
      id: 7,
      description: 'EXAMINATION CHARGES',
      NOS: containerCount > 0 ? containerCount : 0,
      RATE: examRate > 0 ? examRate : 0,
      AMT: examAmt,
    },
    {
      id: 8,
      description: 'CFS GROUND RENT',
      NOS: containerCount > 0 ? containerCount : 0,
      RATE: groundRate > 0 ? groundRate : 0,
      AMT: groundAmt,
    },
    {
      id: 9,
      description: 'SEC49',
      NOS: containerCount > 0 ? containerCount : 0,
      RATE: sec49Rate > 0 ? sec49Rate : 0,
      AMT: sec49Amt,
    },
  ];

  // Legacy calculation formulas:
  // 1. Total Amount: sum of all non-zero item AMTs
  let totalAmt = 0;
  formData.forEach((val) => {
    if (Number(val.AMT) !== 0) {
      totalAmt += Number(val.AMT);
    }
  });

  // 2. Service Total for GST (18%) and TDS (2%): sum of AMTs of specific service categories
  const serviceCategories = [
    'FUMI CHARGES',
    'CFS',
    'CHA & SURVEY',
    'CFS GROUND RENT',
    'SEC49',
    'EXAMINATION CHARGES',
  ];

  const serviceTotalAmt = formData
    .filter((item) => serviceCategories.includes(item.description))
    .reduce((sum, item) => sum + Number(item.AMT || 0), 0);

  const gstTotal = (serviceTotalAmt * 18) / 100;
  const grandTotal = Number(totalAmt) + Number(gstTotal);
  const tdsAmt = Math.round((serviceTotalAmt * 2) / 100);
  const subTotal = grandTotal - tdsAmt;

  // Format numbers cleanly matching legacy software (e.g. 13700 instead of 13700.00 when integer)
  const formatVal = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const num = Number(val);
    if (isNaN(num)) return val;
    return Number.isInteger(num) ? num.toString() : num.toFixed(2);
  };

  return (
    <div className="d-flex flex-column h-100 overflow-hidden overflow-y-auto py-2 py-md-3 px-2 px-md-4">
      <div
        className="d-flex flex-column flex-grow-1 mx-auto w-100"
        style={{ maxWidth: '1280px' }}
      >
        {/* ── Step Progress Timeline — Step 2 active (CLEARING CHARGES) ── */}
        <div className="flex-shrink-0 pb-1">
          <DeTimeline activeStep={2} />
        </div>

        {/* ── Bootstrap 5 two-column layout ── */}
        <div className="row g-3 flex-grow-1 overflow-hidden">
          {/* ── Main Document View (Left Column) ── */}
          <div className="col-12 col-lg-9 d-flex flex-column align-items-center">
            {isLoading ? (
              <div className="card shadow-sm w-100 p-5 text-center text-muted">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                Loading Clearing Charges data...
              </div>
            ) : isError ? (
              <div className="card shadow-sm w-100 p-5 text-center text-danger">
                Failed to load document data. Please try again.
              </div>
            ) : (
              <div className="w-100 d-flex flex-column align-items-center">
                {/* ── Printable PDF Document Area ── */}
                <div id="clear_changess" className="eway-container bg-white border rounded-3 p-3 p-sm-4 shadow-sm w-100 d-flex flex-column" style={{ minHeight: 'calc(100vh - 180px)' }}>
                  {/* File Name Header */}
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3 border-bottom pb-2">
                    <h5 className="fw-bold text-uppercase text-primary mb-0">Clearing Charges</h5>
                    <span className="fw-bold fs-5 text-dark font-monospace text-break">
                      {retrievedData?.fileName || '-'}
                    </span>
                  </div>

                  {/* Document BE NO Details */}
                  <div className="row g-2 mb-4 eway-meta-grid">
                    <div className="col-12 col-md-6">
                      <div className="d-flex align-items-center">
                        <span className="eway-meta-label">BE NO :</span>
                        <span className="eway-meta-value">{retrievedData?.beNo || retrievedData?.billno || '-'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Charges Table matching legacy S.no (val.id) and non-zero display */}
                  <div className="table-responsive border rounded-2 mb-4" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table className="table table-bordered align-middle text-center mb-0 eway-table text-nowrap" style={{ minWidth: '550px' }}>
                      <thead className="gtss-bg-primary text-white text-uppercase">
                        <tr>
                          <th className="text-center" style={{ width: '60px' }}>S.No</th>
                          <th className="text-center">Description</th>
                          <th className="text-center" style={{ width: '90px' }}>NOS</th>
                          <th className="text-center" style={{ width: '120px' }}>Rate</th>
                          <th className="text-center" style={{ width: '140px' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.map((val) => {
                          if (Number(val.AMT) === 0) return null;

                          return (
                            <tr key={val.id}>
                              <td className="text-center fw-semibold">{val.id}</td>
                              <td className="text-center fw-semibold text-dark">{val.description}</td>
                              <td className="text-center">{formatVal(val.NOS)}</td>
                              <td className="text-center font-monospace">{formatVal(val.RATE)}</td>
                              <td className="text-center font-monospace fw-bold">{formatVal(val.AMT)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Summary Totals Block matching legacy labels & numbers 100% */}
                  <div className="row justify-content-end me-1">
                    <div className="col-12 col-md-6 col-lg-5 d-flex flex-column gap-2">
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="eway-meta-label">TOTAL AMOUNT :</span>
                        <span className="eway-meta-value font-monospace">{formatVal(totalAmt)}</span>
                      </div>

                      <div className="d-flex align-items-center justify-content-between">
                        <span className="eway-meta-label">GST :</span>
                        <span className="eway-meta-value font-monospace">{formatVal(gstTotal)}</span>
                      </div>

                      <div className="d-flex align-items-center justify-content-between border-top pt-2">
                        <span className="eway-meta-label text-primary">Total :</span>
                        <span className="eway-meta-value font-monospace text-primary">{formatVal(grandTotal)}</span>
                      </div>

                      <div className="d-flex align-items-center justify-content-between">
                        <span className="eway-meta-label">TDS-2% :</span>
                        <span className="eway-meta-value font-monospace">{tdsAmt}</span>
                      </div>

                      <div className="d-flex align-items-center justify-content-between border-top pt-2">
                        <span className="eway-meta-label text-success fs-6">Sub-Total :</span>
                        <span className="eway-meta-value font-monospace text-success fs-5">{formatVal(subTotal)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Download PDF Button */}
                <div className="my-4 text-center">
                  <button
                    type="button"
                    className="btn btn-orange text-uppercase fw-bold px-4 py-2"
                    onClick={handleDownloadPdf}
                    disabled={isProcessing || isLoading}
                  >
                    {isProcessing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Generating PDF...
                      </>
                    ) : (
                      <>📥 Download PDF</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Right Sidebar Column ── */}
          <div className="col-12 col-lg-3">
            <DeRightSidebar id={id} activeStep={2} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClearingChargesPage;
